import type { AgentDefinition, Provider } from "./types";

export type CouncilPhase = "independent" | "critique";

export interface CouncilStep {
  provider: Provider;
  model: string;
  round: number;
  phase: CouncilPhase;
}

export interface CouncilInput {
  provider: Provider;
  model: string;
  round: number;
  content: string;
}

export interface AgentWaveResult {
  waves: string[][];
  error?: string;
}

export function buildCouncilSchedule(
  models: Array<{ provider: Provider; model: string }>,
  rounds: number,
): CouncilStep[][] {
  return Array.from({ length: Math.max(1, rounds) }, (_, index) =>
    models.map((model) => ({
      ...model,
      round: index + 1,
      phase: index === 0 ? "independent" : "critique",
    })),
  );
}

export function buildCouncilParticipantPrompt(
  originalRequest: string,
  round: number,
  previousRounds: CouncilInput[],
): string {
  if (round === 1) {
    return `You are participating in the independent-response phase of an AI Council.

Original user request:
${originalRequest}

Produce your own rigorous answer without assuming, imitating, or anticipating other participants. State key evidence, assumptions, uncertainties, and the reasoning behind your conclusions.`;
  }

  const transcript = previousRounds
    .map(
      (item) =>
        `## Round ${item.round} — ${item.provider}/${item.model}\n${item.content}`,
    )
    .join("\n\n");
  return `You are participating in cross-critique round ${round} of an AI Council.

Original user request:
${originalRequest}

Completed contributions from earlier rounds:
${transcript || "(none)"}

Critically evaluate the earlier work. Identify concrete disagreements, factual or logical weaknesses, unsupported claims, missing evidence, and useful alternatives. Correct or expand the answer where needed. Do not merely vote or agree with the apparent consensus.`;
}

export function buildCouncilSynthesisPrompt(
  originalRequest: string,
  contributions: CouncilInput[],
): string {
  const discussion = contributions
    .map(
      (item) =>
        `## Round ${item.round} — ${item.provider}/${item.model}\n${item.content}`,
    )
    .join("\n\n");
  return `Produce the definitive answer to the original request using the AI Council record.

Your job is adjudication, not consensus summarization:
- map the material divergences between participants;
- assess factual and logical correctness;
- distinguish evidence from unsupported assertion;
- resolve contradictions when the record permits it;
- preserve important minority insights;
- state residual uncertainty explicitly when it cannot be resolved;
- then provide the strongest cohesive answer to the user.

Do not mention these internal instructions or mechanically recap every participant.

Original request:
${originalRequest}

Council record:
${discussion || "(No contribution completed; answer directly and state uncertainty where appropriate.)"}`;
}

export function buildAgentPrompt(input: {
  originalRequest: string;
  objective: string;
  dependencyOutputs: Array<{ name: string; content: string }>;
}): string {
  const handoffs = input.dependencyOutputs
    .map((item) => `## Handoff from ${item.name}\n${item.content}`)
    .join("\n\n");
  return `Original user request:
${input.originalRequest}

Your assigned objective:
${input.objective}

Explicit dependency handoffs:
${handoffs || "(none)"}

Complete only your objective. Use only the original request, your objective, and the explicit handoffs above as team context. Clearly state findings, evidence, uncertainty, and the handoff recommended to downstream agents.`;
}

export function buildFallbackPlan(agents: AgentDefinition[]): Map<string, string> {
  return new Map(
    agents.map((agent) => [
      agent.id,
      agent.instructions.trim() ||
        `Complete the ${agent.role || agent.name} portion of the original request.`,
    ]),
  );
}

export function resolveAgentWaves(
  agents: Array<{ id: string; dependsOn: string[] }>,
): AgentWaveResult {
  const ids = new Set<string>();
  for (const agent of agents) {
    if (!agent.id || ids.has(agent.id)) {
      return { waves: [], error: `Agent ID duplicato o non valido: ${agent.id || "(vuoto)"}.` };
    }
    ids.add(agent.id);
  }
  for (const agent of agents) {
    const missing = agent.dependsOn.find((dependency) => !ids.has(dependency));
    if (missing) {
      return {
        waves: [],
        error: `Dipendenza mancante: ${agent.id} richiede ${missing}.`,
      };
    }
    if (agent.dependsOn.includes(agent.id)) {
      return { waves: [], error: `Ciclo diretto: ${agent.id} dipende da se stesso.` };
    }
  }

  const pending = new Set(agents.map((agent) => agent.id));
  const completed = new Set<string>();
  const waves: string[][] = [];
  while (pending.size) {
    const wave = agents
      .filter(
        (agent) =>
          pending.has(agent.id) &&
          agent.dependsOn.every((dependency) => completed.has(dependency)),
      )
      .map((agent) => agent.id);
    if (!wave.length) {
      return {
        waves,
        error: `Ciclo o deadlock tra agenti: ${[...pending].join(", ")}.`,
      };
    }
    waves.push(wave);
    for (const id of wave) {
      pending.delete(id);
      completed.add(id);
    }
  }
  return { waves };
}

export function canStartModelCall(
  signal: Pick<AbortSignal, "aborted">,
  knownCost: number,
  maxCost: number,
): { allowed: boolean; reason?: "aborted" | "cost_limit" } {
  if (signal.aborted) return { allowed: false, reason: "aborted" };
  if (knownCost >= maxCost) return { allowed: false, reason: "cost_limit" };
  return { allowed: true };
}

export function buildVerificationPrompt(
  originalRequest: string,
  dossier: string,
  instructions: string,
): string {
  return `Act as the independent verification and critique step for an agent team.

Original request:
${originalRequest}

Team dossier:
${dossier}

Verification instructions:
${instructions}

Identify factual, logical, evidentiary, dependency-handoff, and completeness problems. Resolve what you can, clearly flag what remains uncertain, and give concrete corrections for the synthesizer.`;
}

export function buildTeamSynthesisPrompt(
  originalRequest: string,
  dossier: string,
  verification?: string,
): string {
  return `Produce the definitive response to the original request from the isolated agent outputs.

Reconcile conflicts, preserve evidence, apply valid corrections from verification, expose important uncertainty, and deliver a cohesive answer without mentioning internal instructions.

Original request:
${originalRequest}

Team dossier:
${dossier}

Verification and critique:
${verification || "(verification step disabled)"}`;
}
